import DiseaseEncyclopediaModel from '@/entities/disease-encyclopedia/model/diseaseEncyclopedia';
import { DiseaseEncyclopediaSortType, SearchDiseaseEncyclopediaQuery } from '@/types/bff/gql-schema';
import { SearchResult } from '@/types/search';
import { QueryClient } from '@tanstack/react-query';
import { useSearchDiseaseEncyclopediaQuery } from './search.gql.impl';

const KEYWORD_SEARCH_SIZE = 3;

const parseKeywordIntegratedSearchDiseaseEncyclopediaVo = (
  data: SearchDiseaseEncyclopediaQuery
): SearchResult<DiseaseEncyclopediaModel> => {
  return {
    hasMore: data.searchDiseaseEncyclopedia.items.length > 1,
    elements: data.searchDiseaseEncyclopedia.items.map((diseaseEncyclopedia) => {
      return new DiseaseEncyclopediaModel({
        publicId: diseaseEncyclopedia.publicId,
        content: diseaseEncyclopedia.content,
        title: diseaseEncyclopedia.title,
        titleImageUrl: diseaseEncyclopedia.titleImageUrl,
      });
    }),
    totalPage: data.searchDiseaseEncyclopedia.totalPageCount,
    totalCount: data.searchDiseaseEncyclopedia.totalCount,
  };
};

export enum EKeywordSearchDiseaseEncyclopediaSortType {
  Name = 'NAME',
  Score = 'SCORE',
}

interface GetKeywordIntegratedSearchDiseaseEncyclopedia {
  keywordSearchDiseaseEncyclopedia: SearchResult<DiseaseEncyclopediaModel> | undefined;
  totalPage: number;
  totalCount: number;
  isKeywordSearchDiseaseEncyclopediaLoading: boolean;
}
interface Props {
  keyword?: string;
  sortType?: EKeywordSearchDiseaseEncyclopediaSortType;
  enabled?: boolean;
  limit?: number;
  page?: number;
}

const useGetKeywordIntegratedSearchDiseaseEncyclopedia = (
  props: Props = {}
): GetKeywordIntegratedSearchDiseaseEncyclopedia => {
  const {
    keyword,
    sortType = EKeywordSearchDiseaseEncyclopediaSortType.Name,
    enabled = true,
    limit = KEYWORD_SEARCH_SIZE,
    page = 1,
  } = props;

  const { data: keywordSearchDiseaseEncyclopedia, isLoading: isKeywordSearchDiseaseEncyclopediaLoading } =
    useSearchDiseaseEncyclopediaQuery(
      {
        request: {
          ...(keyword && { keyword }),
          sortType:
            sortType === EKeywordSearchDiseaseEncyclopediaSortType.Name
              ? DiseaseEncyclopediaSortType.Name
              : DiseaseEncyclopediaSortType.Score,
          page: page,
          limit: limit,
        },
      },
      {
        select: parseKeywordIntegratedSearchDiseaseEncyclopediaVo,
        enabled: enabled,
        staleTime: 1000 * 60 * 5, // 5분 - SSR과 동일한 설정
      }
    );

  return {
    keywordSearchDiseaseEncyclopedia,
    totalPage: Math.floor(keywordSearchDiseaseEncyclopedia?.totalPage ?? 0),
    totalCount: keywordSearchDiseaseEncyclopedia?.totalCount ?? 0,
    isKeywordSearchDiseaseEncyclopediaLoading,
  };
};

useGetKeywordIntegratedSearchDiseaseEncyclopedia.prefetchQuery = async (
  queryClient: QueryClient,
  options: {
    keyword?: string;
    limit?: number;
    page?: number;
    sortType?: EKeywordSearchDiseaseEncyclopediaSortType;
  } = {}
) => {
  const {
    keyword,
    limit = KEYWORD_SEARCH_SIZE,
    page = 1,
    sortType = EKeywordSearchDiseaseEncyclopediaSortType.Name,
  } = options;

  return queryClient.prefetchQuery({
    queryKey: useSearchDiseaseEncyclopediaQuery.getKey({
      request: {
        ...(keyword && { keyword }),
        sortType:
          sortType === EKeywordSearchDiseaseEncyclopediaSortType.Name
            ? DiseaseEncyclopediaSortType.Name
            : DiseaseEncyclopediaSortType.Score,
        page: page,
        limit: limit,
      },
    }),
    queryFn: useSearchDiseaseEncyclopediaQuery.fetcher({
      request: {
        ...(keyword && { keyword }),
        sortType:
          sortType === EKeywordSearchDiseaseEncyclopediaSortType.Name
            ? DiseaseEncyclopediaSortType.Name
            : DiseaseEncyclopediaSortType.Score,
        page: page,
        limit: limit,
      },
    }),
    staleTime: 1000 * 60 * 5, // 5분
  });
};

export default useGetKeywordIntegratedSearchDiseaseEncyclopedia;
